import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { StaticTimeAgo } from '@confluence/time-ago';

import { useFetchSpaceSuggestions } from './hooks/useFetchSpaceSuggestions.ts';
import { NeedsReviewSection } from './NeedsReviewSection.tsx';
import { NoSuggestionsFound } from './NoSuggestionsFound.tsx';
import type { SuggestionItem } from './SuggestionCard.tsx';

type SpaceSuggestionsProps = {
	spaceKey: string;
};

export function SpaceSuggestions({ spaceKey }: SpaceSuggestionsProps) {
	const { formatMessage } = useIntl();

	const { suggestions, isLoading, error } = useFetchSpaceSuggestions(spaceKey);

	// Tracks whether the right-rail review panel is open.
	// When true, the main content area shrinks to ~2/3 of the available width.
	const [isRightRailOpen, setIsRightRailOpen] = useState<boolean>(false);

	const openRightRail = useCallback(() => setIsRightRailOpen(true), []);

	const reviewItems: SuggestionItem[] = useMemo(
		() =>
			suggestions.map((suggestion) => ({
				// No icon URL yet — the card renders a styled placeholder box around the empty img.
				iconUrl: '',
				summary: suggestion.title,
				timestamp: <StaticTimeAgo date={suggestion.createdAt} />,
			})),
		[suggestions],
	);

	return (
		<Box xcss={styles.page}>
			{/* Page title */}
			<div css={styles.title}>
				<Heading size="large">{formatMessage(i18n.pageTitle)}</Heading>
			</div>

			{/* Responsive grid: shrinks main content to 2/3 width when right rail is open */}
			<Box xcss={isRightRailOpen ? styles.gridWithRightRail : styles.gridDefault}>
				{/* Main content area */}
				<Box xcss={styles.mainContent}>
					{isLoading && (
						<Box aria-live="polite" testId="space-suggestions-loading">
							<Text>{formatMessage(i18n.loading)}</Text>
						</Box>
					)}
					{!isLoading && error && (
						<Box role="alert" testId="space-suggestions-error">
							<Text>{formatMessage(i18n.error)}</Text>
							{/* Raw cause is exposed only to assistive tech / dev tools for diagnostics. */}
							<VisuallyHidden testId="space-suggestions-error-cause">
								{error.message}
							</VisuallyHidden>
						</Box>
					)}
					{!isLoading && !error && reviewItems.length === 0 && <NoSuggestionsFound />}
					{!isLoading && !error && reviewItems.length > 0 && (
						<NeedsReviewSection items={reviewItems} onReview={openRightRail} />
					)}
				</Box>

				{/* Right rail — placeholder slot; content to be implemented in a separate ticket */}
				{isRightRailOpen && (
					<Box xcss={styles.rightRail} testId="right-rail">
						{/* TODO: Render right-rail review panel here */}
					</Box>
				)}
			</Box>
		</Box>
	);
}

const i18n = defineMessages({
	pageTitle: {
		id: 'space-suggestions.page.title',
		defaultMessage: 'Space suggestions',
		description: 'The main heading for the Space Suggestions page',
	},
	loading: {
		id: 'space-suggestions.page.loading',
		defaultMessage: 'Loading…',
		description: 'Shown while the Space Suggestions page is fetching data',
	},
	error: {
		id: 'space-suggestions.page.error',
		defaultMessage: 'Something went wrong while loading suggestions. Please try again later.',
		description:
			'Shown when the Space Suggestions page fails to fetch data from the backend. Generic message — the raw error text is only surfaced via the element title attribute for diagnostics.',
	},
});

const styles = cssMap({
	page: {
		paddingBlockStart: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
	},
	title: {
		marginBottom: token('space.300'),
	},
	mainContent: {
		minWidth: '0',
	},
	rightRail: {
		minWidth: '0',
	},
	/**
	 * Grid layout variants:
	 * - gridDefault: main content takes full width
	 * - gridWithRightRail: main content takes ~2/3, right rail takes ~1/3
	 */
	gridDefault: {
		display: 'grid',
		gridTemplateColumns: '1fr',
	},
	gridWithRightRail: {
		display: 'grid',
		gridTemplateColumns: '2fr 1fr',
	},
});
